/**
 * RestClientServiceProvider
 *
 * A service provider for handling REST API requests with authentication, token refresh, and caching.
 *
 * Component Structure:
 * └─ RestClientServiceProvider
 *    ├─ Initialization
 *    │  ├─ Constructor (Sets up core, token, and base URL)
 *    │  ├─ providers (Registers service)
 *    │  └─ init (Initializes service instance)
 *    │
 *    ├─ Request Handling
 *    │  ├─ makeRequest (Main entry point for all requests)
 *    │  ├─ handleResponseStatus (Handles response status)
 *    │  ├─ handleRefresh (Handles token refresh)
 *    │  ├─ handleSessionExpired (Handles session expiration)
 *    │  └─ handleTokenInvalid (Handles token refresh failure)
 *    │  ├─ getMemoizedData (Caches API responses for reuse)
 *    │  └─ clearMemoizedData (Clears cached data)
 *    │
 *    ├─ Authentication
 *    │  ├─ authenticate (Handles user authentication)
 *    │  ├─ profile (Fetches user profile)
 *    │  ├─ handleRefresh (Refreshes authentication token)
 *    │  ├─ handleSessionExpired (Handles session expiration)
 *    │  └─ handleTokenInvalid (Manages token refresh flow)
 *    │
 *    ├─ Request Methods
 *    │  ├─ GET (Standard data retrieval)
 *    │  ├─ POST/PUT (Data submission with JSON)
 *    │  ├─ FilePost (File uploads with FormData)
 *    │  ├─ FileUpload (Multiple file handling)
 *    │  └─ DELETE (Resource deletion)
 *    │
 *    └─ Utility Methods
 *       ├─ errorMessage (Displays error messages)
 *
 * Core Responsibilities:
 * 1. Handle HTTP requests (GET, POST, PUT, DELETE, File uploads)
 * 2. Manage authentication tokens and refresh flows
 * 3. Handle response status, errors, and session expiration
 * 4. Cache API responses for optimized performance
 *
 * Request Flow:
 * makeRequest -> handleResponseStatus -> handleRefresh -> handleSessionExpired -> handleTokenInvalid
 *
 * Configuration:
 * - MAX_ATTEMPTS: Maximum number of retry attempts (default: 5)
 * - LOGOUT_ATTEMPT_THRESHOLD: Attempts before forced logout (default: 3)
 */

import { ServiceProvider } from "@osjs/common";
import LocalStorageAdapter from "./localStorageAdapter.js";
import { SSE } from "sse.js";

const MAX_ATTEMPTS = 5;
const LOGOUT_ATTEMPT_THRESHOLD = 3;

export class RestClientServiceProvider extends ServiceProvider {
  constructor(core, options = {}) {
    super(core, options || {});
    this.core = core;
    this.token = null;
    this.baseUrl = this.core.config("wrapper.url");
    this.store = {}; // { [APP_ID] : { [DATA_NAME] : DATA } }
    this.sseInstance = null;
  }

  providers() {
    return ["oxzion/restClient"];
  }

  async init() {
    this.core.instance("oxzion/restClient", () => ({
      request: (version, action, params, method, headers, raw, attempt, signal) =>
        this.makeRequest(version, action, params, method, headers, raw, attempt, signal),
      authenticate: (params) => this.authenticate(params),
      profile: () => this.profile(),
      handleRefresh: () => this.handleRefresh(),
      clearMemoizedData: (...args) => this.clearMemoizedData(...args),
      getMemoizedData: (...args) => this.getMemoizedData(...args),
    }));
  }

  // profile wrapper
  profile() {
    let userData = this.core.getUser();
    this.token = userData["jwt"];
    try {
      let url = this.baseUrl + "user/me/a+p+acc+ap+t";
      var xmlHttp = new XMLHttpRequest();
      xmlHttp.open("GET", url, false);
      let auth = "Bearer " + this.token;
      xmlHttp.setRequestHeader("content-type", "application/json");
      xmlHttp.setRequestHeader("Authorization", auth);
      xmlHttp.send(null);
      return xmlHttp.responseText;
    } catch (e) {}
  }

  async handleRefresh() {
    let user = this.core.getUser();
    var lsHelper = new LocalStorageAdapter();
    lsHelper.supported();

    if (user["jwt"] != null) {
      const rtoken = lsHelper.get("REFRESH_token");
      let jwt = user["jwt"];
      let formData = new FormData();
      formData.append("jwt", jwt);
      formData.append("refresh_token", rtoken["key"]);

      try {
        let response = await fetch(this.baseUrl + "refreshtoken", {
          method: "POST",
          body: formData,
          credentials: "include",
        });

        let data = await response.json();

        if (data["status"] === "success") {
          jwt = data["data"]["jwt"];
          let refresh = data["data"]["refresh_token"];
          user["jwt"] = jwt;
          this.core.setUser(user);
          lsHelper.set("AUTH_token", jwt);
          lsHelper.set("REFRESH_token", refresh);
          return true;
        } else {
          this.core.make("osjs/auth").logout();
        }
      } catch (error) {
        console.error("Error refreshing token:", error);
        this.core.make("osjs/auth").logout();
      }
    } else {
      this.core.make("osjs/auth").logout();
    }
  }

  // handles all request to OXZion apps
  // version - string
  // action - string
  // params - *
  // method - string
  async makeRequest(version, action, params, method, headers = {}, raw = false, attempt = 1, signal) {
    if (attempt > MAX_ATTEMPTS) {
      localStorage.clear();
      location.reload();
      return;
    }
    try {
      this.token = this.core.getUser().jwt;
    } catch (error) {
      if ((this.token === undefined || attempt == LOGOUT_ATTEMPT_THRESHOLD) && typeof this.core !== "undefined") {
        this.core?.make("osjs/auth")?.logout();
        return;
      }
    }

    if (action.charAt(0) == "/") action = action.substr(1);
    const urlString = action.includes("https:") ? action : this.baseUrl + action;

    let reqHeaders = {
      Authorization: "Bearer " + this.token,
      ...headers,
    };

    // SSE Support
    if (method === "SSE") {
      const { callbacks = {}, payload = {} } = params;

      // Close any existing SSE connection
      if (this.sseInstance) {
        this.sseInstance.close();
        this.sseInstance = null;
      }

      this.sseInstance = new SSE(urlString, {
        headers: {
          ...reqHeaders,
          "Content-Type": "text/json",
          Accept: "text/event-stream",
        },
        method: params.method ? params.method : "POST",
        payload: JSON.stringify(payload),
        maxRetries: 1,
        reconnectDelay: 3000,
      });

      this.sseInstance.onopen = () => callbacks.onOpen?.();

      const handleEvent = (handler, event) => {
        try {
          const data = JSON.parse(event.data);
          handler?.(data);
        } catch (err) {
          handler?.(err);
        }
      };

      this.sseInstance.addEventListener("progress", (event) => {
        handleEvent(callbacks.onUpdateStatus, event);
      });

      this.sseInstance.addEventListener("success", (event) => {
        this.sseInstance.close();
        this.sseInstance = null;
        handleEvent(callbacks.onSuccess, event);
      });

      this.sseInstance.addEventListener("error", (event) => {
        this.sseInstance.close();
        this.sseInstance = null;
        handleEvent(callbacks.onError, event);
      });

      this.sseInstance.onerror = (error) => {
        this.sseInstance.close();
        this.sseInstance = null;
        callbacks.onError?.(error);
      };

      this.sseInstance.stream();
      return;
    }

    let reqParams = {
      method: method,
      credentials: "include",
      headers: reqHeaders,
      ...(signal && { signal }),
    };

    method = method.toLowerCase();
    try {
      if (method === "post" || method === "put") {
        reqHeaders["Content-Type"] = "application/json";
        reqParams.body = params ? JSON.stringify(params) : null;
      } else if (method === "filepost") {
        let formData = new FormData();
        for (let k in params) {
          if (params[k] && params[k].name && params[k].body) {
            formData.append(k, params[k].body, params[k].name);
          } else {
            formData.append(k, params[k]);
          }
        }
        reqParams.body = formData;
        reqParams.method = "post";
      } else if (method === "fileupload") {
        let formData = new FormData();
        if (params.file) {
          formData.append("file", params.file);
          for (let k in params.data) formData.append(k, params.data[k]);
        } else {
          const { files, filesData } = params;
          files?.map((file) => formData.append("files[]", file));
          filesData?.map((fileData) => formData.append("data[]", JSON.stringify(fileData)));
        }
        reqParams.body = formData;
        reqParams.method = "post";
      } else if (method === "delete") {
        reqParams.body = params ? JSON.stringify(params) : null;
      }
      let resp = await fetch(urlString, reqParams);
      if (resp.status == 400 || resp.status == 401 || resp.status == 404) {
        // fall through to refresh handling
        let that = this;
        let respData = await resp.json();
        if (respData.status === "error") {
          if (respData.message === "Session Expired") {
            that.core.make("osjs/auth").logout();
          } else if (respData.message === "Token Invalid") {
            if (await that.handleRefresh()) return that.makeRequest(version, action, params, method, headers, raw, attempt + 1);
          } else return respData;
        } else return respData;
      } else {
        // success flow
        if (resp.status === 204) {
          // 204 responses have no body to parse - send success from here.
          return { success: true };
        } else if (raw || !resp.headers.get("Content-Type").includes("json")) {
          return resp;
        }
        return resp.json();
      }
    } catch (e) {
      return Promise.reject(e);
    }
  }

  errorMessage(message) {
    this.messageBox.show(message, "", "OK", false).then((response) => {
      return response;
    });
  }

  clearMemoizedData(appId, dataName) {
    return delete this.store[appId]?.[dataName];
  }

  async getMemoizedData(appId, dataName, url, method = "get", payload = {}) {
    if (this.store[appId]?.[dataName]) return this.store[appId][dataName];
    const response = await this.makeRequest("v1", url, payload, method);
    if (response.status === "success") {
      if (!this.store[appId]) this.store[appId] = {};
      this.store[appId][dataName] = response;
    }
    return response;
  }
}
